import { ShoppingCartSimpleIcon, TruckIcon, TShirtIcon, UserCircleIcon } from '@phosphor-icons/react'

import { PackageReturnIcon, shipmentStatusLabels, trackingStatuses, trackingStatusLabels } from '@reveni-io/reveni-ui'

import {
  displayMatchTypes,
  fullTextMatchTypes,
  idMatchTypes,
  numberMatchTypes,
  numericComparisonMatchTypes,
  regionMatchTypes,
} from '@/common/components/MatchTypeEditor/constants'
import { getReasonMenuItems } from '@/common/helpers/helpers'
import { filterDisabledProperties, getBaseProductProperties } from '@/common/helpers/rules/helpers'
import { parcelDirections } from '@/parcels/constants'

import { shipmentsAppliesWhenMenuItems, shipmentStatusValues } from './constants'

const getProductStatusComboItems = t => {
  return Object.values(trackingStatuses).map(status => ({ value: status, label: t(trackingStatusLabels[status]) }))
}

const getProductInOrderProperties = ({ productTypes, productCategories, productCollections, t }) => {
  return {
    ...getBaseProductProperties({ productTypes, productCategories, productCollections }),
    product_status: {
      label: 'Estado del producto',
      matchTypes: idMatchTypes,
      comboItems: getProductStatusComboItems(t),
    },
  }
}

const getProductToReturnProperties = ({
  productTypes,
  productCategories,
  productCollections,
  returnReasons,
  language,
}) => {
  return {
    ...getBaseProductProperties({ productTypes, productCategories, productCollections }),
    return_reason: {
      label: 'Motivo de devolución',
      matchTypes: idMatchTypes,
      comboItems: getReasonMenuItems(returnReasons, language),
    },
  }
}

export const getConditionTypes = ({
  productTypes,
  productCategories,
  productCollections,
  returnReasons,
  regions,
  language,
  disabledConditionFields,
  t,
}) => {
  const conditionTypes = {
    order: {
      label: 'Pedido',
      description: 'Evalúa las propiedades del pedido original.',
      icon: ShoppingCartSimpleIcon,
      scope: 'order',
      properties: {
        order_name: { label: 'Número de pedido', matchTypes: [...numberMatchTypes, displayMatchTypes.valueInRange] },
        order_date: {
          label: 'Fecha de pedido',
          matchTypes: [
            displayMatchTypes.olderThan,
            displayMatchTypes.newerThan,
            displayMatchTypes.dateInRange,
            displayMatchTypes.notDateInRange,
          ],
        },
        region: { label: 'Región del pedido', matchTypes: regionMatchTypes, comboItems: regions },
        status: { label: 'Estado del pedido', matchTypes: idMatchTypes },
        order_type: {
          label: 'Tipo de pedido',
          matchTypes: idMatchTypes,
          comboItems: [
            { value: 'initial', label: t('Pedido inicial') },
            { value: 'exchange', label: t('Pedido de cambio') },
          ],
        },
        order_net_value: { label: 'Valor neto del pedido', matchTypes: numericComparisonMatchTypes },
        order_tag: { label: 'Etiqueta del pedido', matchTypes: fullTextMatchTypes },
        order_source: { label: 'Origen del pedido', matchTypes: fullTextMatchTypes },
        has_discounts: { label: '¿Tiene descuentos aplicados?', matchTypes: [displayMatchTypes.boolean] },
        discount_applied: { label: 'Descuento aplicado', matchTypes: idMatchTypes },
      },
    },
    customer: {
      label: 'Cliente',
      description: 'Evalúa las propiedades del cliente que realizó el pedido.',
      icon: UserCircleIcon,
      scope: 'customer',
      properties: {
        customer_email: { label: 'Email del cliente', matchTypes: fullTextMatchTypes },
        customer_tag: { label: 'Etiqueta del cliente', matchTypes: fullTextMatchTypes },
        customer_group: { label: 'Grupo del cliente', matchTypes: idMatchTypes },
      },
    },
    productInOrder: {
      label: 'Producto en el pedido',
      description:
        'Evalúa las propiedades de los productos del pedido original, incluyendo los productos que no se están devolviendo.',
      icon: TShirtIcon,
      scope: 'item',
      itemContext: 'in_order',
      supportsAppliesWhen: true,
      properties: getProductInOrderProperties({ productTypes, productCategories, productCollections, t }),
    },
    productToReturn: {
      label: 'Producto a devolver',
      description: 'Evalúa las propiedades de los productos seleccionados para devolución o cambio.',
      icon: PackageReturnIcon,
      scope: 'item',
      itemContext: 'to_be_returned',
      supportsAppliesWhen: true,
      properties: getProductToReturnProperties({
        productTypes,
        productCategories,
        productCollections,
        returnReasons,
        language,
      }),
    },
    shipments: {
      label: 'Envíos',
      description: 'Evalúa las propiedades de los envíos del pedido, tanto de salida como de devolución.',
      icon: TruckIcon,
      scope: 'parcel',
      supportsAppliesWhen: true,
      appliesWhenMenuItems: shipmentsAppliesWhenMenuItems,
      properties: {
        parcel_type: {
          label: 'Tipo de envío',
          matchTypes: idMatchTypes,
          comboItems: [
            { value: parcelDirections.outbound, label: t('Envío') },
            { value: parcelDirections.inbound, label: t('Devolución') },
          ],
        },
        status: {
          label: 'Estado del envío',
          matchTypes: idMatchTypes,
          comboItems: shipmentStatusValues.map(status => ({ value: status, label: t(shipmentStatusLabels[status]) })),
        },
      },
    },
  }

  return filterDisabledProperties(conditionTypes, disabledConditionFields)
}
